<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      事業所への評価
    </Header>

    <div class="application-evaluation p-3">
      <div class="py-2 mb-3" data-testid="about-confirm-evaluation">
        <dl class="rule-list">
          <dt class="text-weight-500 text-green text-size-14 mb-1 caution">
            <FeatherIcon
              icon="info"
              size="16"
              class="caution-icon"
            />事業所はこの内容を見て評価することはありません(お互いの評価完了後に評価内容を閲覧できるようになります)<br />
          </dt>
          <dt class="text-weight-500 text-green text-size-14 mb-1 caution">
            <FeatherIcon
              icon="info"
              size="16"
              class="caution-icon"
            />評価内容は匿名化され、公開されます
          </dt>
        </dl>
      </div>
      <CustomValidationObserver ref="vObserver" v-slot="{ handleSubmit }">
        <BForm @submit.stop.prevent>
          <div class="star-section">
            <div>
              <div class="d-flex align-items-center">
                <p class="text-yellow text-weight-500">1</p>
                <p class="ml-2">予定通りに、始業・休憩・終業はされたか？</p>
              </div>
              <ValidationProvider
                v-slot="validationState"
                name="評価項目1"
                :rules="{ required: true }"
                vid="scoreScheduling"
              >
                <FiveInputStars
                  v-model="scoreScheduling"
                  :validationState="validationState"
                  class="star-block"
                />
              </ValidationProvider>
            </div>
            <div class="mt-3">
              <div class="d-flex align-items-center">
                <p class="text-yellow text-weight-500">2</p>
                <p class="ml-2">掲載の仕事内容通りでズレはなかったか？</p>
              </div>
              <ValidationProvider
                v-slot="validationState"
                name="評価項目2"
                :rules="{ required: true }"
                vid="scoreCorrectness"
              >
                <FiveInputStars
                  v-model="scoreCorrectness"
                  :validationState="validationState"
                  class="star-block"
                />
              </ValidationProvider>
            </div>
            <div class="mt-3">
              <div class="d-flex align-items-center">
                <p class="text-yellow text-weight-500">3</p>
                <p class="ml-2">仕事手順の指示はあったか？</p>
              </div>
              <ValidationProvider
                v-slot="validationState"
                name="評価項目3"
                :rules="{ required: true }"
                vid="scoreInstructions"
              >
                <FiveInputStars
                  v-model="scoreInstructions"
                  :validationState="validationState"
                  class="star-block"
                />
              </ValidationProvider>
            </div>
          </div>
          <div class="mt-3">
            <div class="d-flex align-items-center">
              <p class="text-yellow text-weight-500">4</p>
              <p class="ml-2">職員からの挨拶はあったか？</p>
            </div>
            <ValidationProvider
              v-slot="validationState"
              name="評価項目4"
              :rules="{ required: true }"
              vid="scoreGreeting"
            >
              <FiveInputStars
                v-model="scoreGreeting"
                :validationState="validationState"
                class="star-block"
              />
            </ValidationProvider>
          </div>
          <div class="mt-3">
            <div class="d-flex align-items-center">
              <p class="text-yellow text-weight-500">5</p>
              <p class="ml-2">清掃は行き届いていたか？</p>
            </div>
            <ValidationProvider
              v-slot="validationState"
              name="評価項目5"
              :rules="{ required: true }"
              vid="scoreCleanliness"
            >
              <FiveInputStars
                v-model="scoreCleanliness"
                :validationState="validationState"
                class="star-block"
              />
            </ValidationProvider>
          </div>

          <ValidationProvider
            v-slot="validationState"
            name="良かった点"
            :rules="{ required: true, max: 2000 }"
            vid="goodPoint"
            class="mt-4"
          >
            <label class="mr-1 mb-1 text-size-14 text-mild">良かった点</label>
            <AppLabel variant="red">必須</AppLabel>
            <span class="ml-2 text-danger text-size-12"
              >※個人名は記載しないでください</span
            >
            <TextAreaGroup
              v-model="goodPoint"
              placeholder="例：スタッフの方々が優しく対応をしてくださり、楽しく働くことができました。仕事の説明なども丁寧で非常に働きやすかったです。また勤務したいと思える事業所様です。"
              :validationState="validationState"
            />
          </ValidationProvider>
          <ValidationProvider
            v-slot="validationState"
            name="改善点"
            :rules="{ required: true, max: 2000 }"
            vid="badPoint"
            class="mt-3"
          >
            <label class="mr-1 mb-1 text-size-14 text-mild">改善点</label>
            <AppLabel variant="red">必須</AppLabel>
            <span class="ml-2 text-danger text-size-12"
              >※個人名は記載しないでください</span
            >
            <TextAreaGroup
              v-model="badPoint"
              placeholder="例：業務に必要な物品の位置や利用者様のリスク管理についてのご説明をいただけると、初めてでも安心して勤務できると感じました。"
              :validationState="validationState"
            />
          </ValidationProvider>

          <AppButton
            :block="true"
            class="mt-4"
            @click="handleSubmit(openConfirmModal)"
            >評価を登録する</AppButton
          >
        </BForm>
      </CustomValidationObserver>
    </div>
    <BModal
      :visible="isShownConfirmModal"
      title="評価の最終確認"
      header-class="font-weight-bold modal-responsive-title"
      ok-title="確定する"
      cancel-title="キャンセルする"
      cancel-variant="gray-03"
      @ok="onClickSubmitButton"
      @cancel="closeConfirmModal"
      @hide="closeConfirmModal"
      centered
    >
      <div class="mb-2">評価が確定されます<br />よろしいですか？</div>
      <p class="text-danger">
        ・事業所はこの内容を見て評価することはありません(お互いの評価完了後に評価内容を閲覧できるようになります)
      </p>
      <p class="text-danger">・評価は後から変更することはできません</p>
    </BModal>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import AppLabel from "@/components/atoms/input/AppLabel.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import TextAreaGroup from "@/components/molecules/form/TextAreaGroup.vue";
import FiveInputStars from "@/components/molecules/form/FiveInputStars.vue";
import AppButton from "@/components/atoms/button/AppButton.vue";
import { orderService } from "@/app/services/OrderService";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { EvaluationByWorker } from "@/types/order/EvaluationByWorker";

@Component({
  components: {
    AppLabel,
    FeatherIcon,
    Header,
    TextAreaGroup,
    FiveInputStars,
    AppButton
  }
})
export default class ApplicationEvaluation extends Vue {
  scoreScheduling: number | null = null;
  scoreCorrectness: number | null = null;
  scoreInstructions: number | null = null;
  scoreGreeting: number | null = null;
  scoreCleanliness: number | null = null;
  goodPoint = "";
  badPoint = "";
  isShownConfirmModal = false;

  get applicationId() {
    return this.$route.params.applicationId;
  }

  onClickBack() {
    this.$router.back();
  }

  async onClickSubmitButton() {
    this.$loading.show();
    try {
      const evaluationByWorker: EvaluationByWorker = await orderService.evaluationByWorker(
        {
          application: Number(this.applicationId),
          scoreScheduling: this.scoreScheduling!,
          scoreCorrectness: this.scoreCorrectness!,
          scoreInstructions: this.scoreInstructions!,
          scoreGreeting: this.scoreGreeting!,
          scoreCleanliness: this.scoreCleanliness!,
          goodPoint: this.goodPoint,
          badPoint: this.badPoint
        }
      );
      await this.$router.push({
        name: "EvaluationDesireToWork",
        params: {
          applicationId: this.applicationId,
          evaluationByWorkerId: String(evaluationByWorker.id)
        }
      });
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  openConfirmModal() {
    this.isShownConfirmModal = true;
  }

  closeConfirmModal() {
    this.isShownConfirmModal = false;
  }
}
</script>

<style lang="scss" scoped>
.application-evaluation {
  .star-block {
    padding: 6px 0 0 25px;
  }
}

.rule-list {
  padding: 12px;
  font-size: 14px;
  background-color: $primary-bg;
  border: 1px solid $yellow;
  border-radius: 4px;

  &-text {
    margin-bottom: 0;
    margin-left: 22px;
    color: $mild;
  }
}

.caution {
  position: relative;
  padding-left: 22px;
}

.caution-icon {
  position: absolute;
  top: 2px;
  left: 0;
}
</style>
